import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TranslationKey } from '@finance/contracts';
import {
  createStaticTranslator,
  type StaticTranslator,
  type TranslationOptions,
} from './static-translator';
import {
  resolveRuntimeTKey,
  resolveTKey,
  translateRuntimeTKeyOrFallback,
} from './resolve-key';

export interface AppI18n {
  text: (key: TranslationKey, options?: TranslationOptions) => string;
  runtimeText: (
    key: string | undefined | null,
    options?: TranslationOptions,
    fallbackKey?: TranslationKey,
  ) => string;
  optionalRuntimeText: (
    key: string | undefined | null,
    options?: TranslationOptions,
  ) => string | undefined;
  /** Typed translator capability for descriptor-driven reusable renderers. */
  translator: StaticTranslator;
  language: string;
  changeLanguage: (language: string) => Promise<unknown>;
}

/** Application-facing localization API for static and descriptor/runtime copy. */
export function useAppI18n(): AppI18n {
  const { t, i18n } = useTranslation();
  const translator = useMemo(() => createStaticTranslator(t), [t]);

  const text = useCallback(
    (key: TranslationKey, options?: TranslationOptions) =>
      translator(resolveTKey(key), options),
    [translator],
  );

  const runtimeText = useCallback(
    (
      key: string | undefined | null,
      options?: TranslationOptions,
      fallbackKey?: TranslationKey,
    ) => translateRuntimeTKeyOrFallback(translator, key, options, fallbackKey),
    [translator],
  );

  const optionalRuntimeText = useCallback(
    (key: string | undefined | null, options?: TranslationOptions) => {
      const resolved = resolveRuntimeTKey(key);
      return resolved ? translator(resolved, options) : undefined;
    },
    [translator],
  );

  return {
    text,
    runtimeText,
    optionalRuntimeText,
    translator,
    language: i18n.language,
    changeLanguage: (language: string) => i18n.changeLanguage(language),
  };
}
